import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/App";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();

  // Redirect if already authenticated (e.g. visiting /login while logged in)
  useEffect(() => {
    if (isAuthenticated && user && !loading && !loginSuccess) {
      const dest = user.role === "admin" ? "/admin" : "/app/home";
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, user, loading, loginSuccess, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        // Mark login success — show loading state while navigation happens
        setLoginSuccess(true);
        const dest = result.user.role === "admin" ? "/admin" : "/app/home";
        navigate(dest, { replace: true });
      } else {
        setLoading(false);
        setError(result.error);
      }
    } catch (err) {
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  };

  // Show branded loading screen after successful login
  if (loginSuccess) {
    return (
      <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
        <Logo size="md" />
        <div style={{ width: "32px", height: "32px", border: "3px solid #FACC15", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>Loading your dashboard...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <Helmet>
        <title>Login — ChillRefer</title>
        <meta name="description" content="Login to your ChillRefer account to manage your AI-powered LinkedIn referral campaigns." />
        <link rel="canonical" href="https://chillrefer.com/login" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div style={{ width: "100%", maxWidth: "400px" }}>
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none", color: "#fff", marginBottom: "24px" }} data-testid="login-logo">
            <Logo size="md" />
          </Link>
          <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginTop: "20px", color: "#fff" }}>Welcome back</h1>
          <p style={{ color: "#52525b", marginTop: "6px", fontSize: "0.85rem" }}>Sign in to your ChillRefer account</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {error && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", padding: "10px 14px", color: "#ef4444", fontSize: "0.85rem" }} data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label style={{ display: "block", fontSize: "0.78rem", color: "#71717a", fontWeight: 500, marginBottom: "6px" }}>Email</label>
            <input
              data-testid="login-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input"
              style={{ width: "100%", padding: "12px 14px", fontSize: "1rem" }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.78rem", color: "#71717a", fontWeight: 500, marginBottom: "6px" }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
                style={{ width: "100%", padding: "12px 14px", paddingRight: "42px", fontSize: "1rem" }}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#71717a", cursor: "pointer" }}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <Link to="/forgot-password" style={{ fontSize: "0.78rem", color: "#FACC15", textDecoration: "none" }}>Forgot password?</Link>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              background: loading ? "#a3800f" : "#FACC15",
              color: "#000",
              fontWeight: 700,
              fontSize: "1rem",
              border: "none",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>Sign In <ArrowRight size={16} /></>}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "24px", color: "#52525b", fontSize: "0.85rem" }}>
          Don't have an account?{" "}
          <Link to="/register" style={{ color: "#FACC15", textDecoration: "none", fontWeight: 600 }}>Sign up</Link>
        </p>
      </div>
    </div>
  );
}
